import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import Select from "components/common/Select";
import IcdAutocomplete from "../chiefComplaints/IcdAutocomplete";
import { ChipPlusBoldIcon, SparkleMarkIcon } from "../labInvestigations/labIcons";
import FamilyHistoryService from "./FamilyHistoryService";
import {
  COMMON_CONDITIONS,
  RELATIONSHIPS,
  isSingleRelationship,
  relationshipLabel,
} from "./data";
import { FAM_V2_REFRESH } from "./events";

/**
 * ONE relative sheet — add or edit — owning all of its own field state.
 *
 * This used to be the body of RelativeEditor, with every field hoisted into
 * that component and gated on `formOpen`. It was split out so the rail can
 * hold a STACK of sheets: each card needs its own relationship / conditions /
 * description, and sibling cards must not see each other's values. The rail
 * (RelativeEditor) now only decides which cards exist and in what order;
 * everything below the card border lives here.
 *
 * Every rule from the original dialog (and then the single-form rail) is
 * carried over verbatim — nothing here is new behaviour:
 *   • single-only relationships already on file are disabled in the dropdown
 *     ("Already added") and hard-block Save if selected — the row being
 *     edited excluded, so re-saving it never self-blocks
 *   • the relationship itself is fixed once saved (the Select is disabled on
 *     edit); change it by deleting and re-adding
 *   • "Other" requires a customRelation, which may neither shadow a builtin
 *     label nor duplicate another "other" relative (self excluded again)
 *   • conditions are a dedup'd chip list fed by free text (Enter, or the
 *     field's search button) plus the common quick-picks
 *   • description keeps the 250-word cap
 *   • an entry must carry at least one of Conditions / Description
 *
 * The one structural difference the stack forces: the existing-relatives read
 * that powers the duplicate rules used to run BEFORE the rail opened, because
 * one component owned both the fetch and the form. A sheet cannot fetch before
 * it exists, so it runs on mount here and Save stays disabled ("Loading…")
 * until it settles — the duplicate rules are never judged against an empty
 * list. It re-runs on FAM_V2_REFRESH so a sibling sheet's save cannot leave
 * this one's "Already added" hints stale.
 */

// Description word cap — matches Diagnosis / Hospitalisation.
const MAX_DESCRIPTION_WORDS = 250;
const countWords = (s) => {
  const t = (s || "").trim();
  return t ? t.split(/\s+/).length : 0;
};
const clampWords = (next, max) => {
  if (countWords(next) <= max) return next;
  const parts = next.split(/(\s+)/);
  let words = 0;
  const out = [];
  for (const chunk of parts) {
    if (/^\s+$/.test(chunk)) { out.push(chunk); continue; }
    if (chunk === "") continue;
    if (words >= max) break;
    out.push(chunk);
    words += 1;
  }
  return out.join("").replace(/\s+$/, "");
};

// Dedup + trim helper for the pill-based conditions editor. Dedup key is
// the ICD-10 code when we have one (multiple labels can share a code — e.g.
// "Diabetes mellitus, unspecified" vs "Diabetes mellitus type 2" both live
// under E11.9 in some catalogs); when the ICD is empty (legacy free-text
// rows carried in on an edit) fall back to case-insensitive name.
const upsertCondition = (list, name, icdCode, source) => {
  const clean = (name || "").trim();
  if (!clean) return list;
  const code = (icdCode || "").trim();
  const dup = list.some((c) => {
    const cCode = (c.icdCode || "").trim();
    if (code && cCode) return cCode === code;
    return c.name.toLowerCase() === clean.toLowerCase();
  });
  if (dup) return list;
  return [...list, { name: clean, icdCode: code, source: source || "icd" }];
};

const removeCondition = (list, name) =>
  list.filter((c) => c.name.toLowerCase() !== (name || "").toLowerCase());

/**
 * Coerce whatever the list endpoint returned into a clean array of relative
 * objects. Byte-for-byte the same guard FamilyHistory.js applies to the same
 * payload (it is not exported there, so it is restated rather than imported):
 * `relatives` has arrived as a keyed object at least once, which made every
 * `.map`/`for…of` over it throw and took the WHOLE patient screen down. The
 * duplicate rules below iterate this list on every render, so the rail needs
 * the identical guard.
 */
const toRelativeList = (raw) => {
  let list = raw;
  if (!Array.isArray(list)) {
    list = list && typeof list === "object" ? Object.keys(list).map((k) => list[k]) : [];
  }
  return list.filter((r) => r && typeof r === "object");
};

/**
 * relationshipLabel falls back to the raw `relationship` value, which is an
 * object on some payloads. That string reaches JSX (the sheet's sub-title) and
 * the rail's live region, and React throws "Objects are not valid as a React
 * child" for anything object-shaped — a white screen, not a broken word. Same
 * reason FamilyHistory.js runs the label through asText before rendering it.
 *
 * Exported because RelativeEditor names sheets with it too.
 */
export const safeRelationshipLabel = (relative) => {
  const label = relationshipLabel(relative);
  if (typeof label === "string") return label.trim();
  if (typeof label === "number") return String(label);
  if (label && typeof label === "object") {
    const named = label.name || label.label || label.displayName;
    return typeof named === "string" ? named.trim() : "";
  }
  return "";
};

// The single-only relationships a list already accounts for.
const takenFrom = (list) =>
  new Set(
    (list || [])
      .filter((r) => isSingleRelationship(r.relationship))
      .map((r) => r.relationship)
  );

// Add-mode default: prefer Father, else the first relationship that is not
// already taken — spares the user an immediate duplicate error.
const firstOpenRelationship = (taken) => {
  if (!taken.has("father")) return "father";
  const firstOpen = RELATIONSHIPS.find(
    (o) => !isSingleRelationship(o.key) || !taken.has(o.key)
  );
  return (firstOpen && firstOpen.key) || "other";
};

// Map a loose AI-emitted relationship string to the RELATIONSHIPS enum
// key. Anything unrecognised (uncle, cousin, aunt, ...) becomes "other"
// with the raw text moving into customRelation. Mirrors the Go adapter's
// `normaliseRelationship` so both paths agree.
const VALID_REL_KEYS = new Set([
  "father", "mother", "brother", "sister",
  "maternal-grandmother", "maternal-grandfather",
  "paternal-grandmother", "paternal-grandfather",
  "other",
]);
const seedRelationshipFromPayload = (p) => {
  const raw = (p && (p.relationship || p.value || "")).toString();
  const t = raw.trim().toLowerCase().replace(/_/g, "-").replace(/\s+/g, "-");
  if (VALID_REL_KEYS.has(t) && t !== "other") {
    return { relationship: t, customRelation: (p && p.custom_relation) || "" };
  }
  const custom = (p && (p.custom_relation || p.customRelation || raw)) || "";
  return { relationship: "other", customRelation: custom };
};

// Normalise conditions off the AI payload into the {name, icdCode, source}
// shape this form holds. ICD-only — free-text conditions are dropped so
// the reviewer never sees a chip that can't survive the save (the ICD
// picker downstream requires a code, and the wire format demands one).
// Two rows with the same ICD (or same lowered name) collapse into one so
// the AI's duplicate emits don't stack as separate chips.
const seedConditionsFromPayload = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw.reduce((acc, c) => {
    const obj = c && typeof c === "object" ? c : { name: String(c || "") };
    const name = (obj.name || "").toString().trim();
    const icdCode = (obj.icdCode || obj.icd_code || obj.code || "")
      .toString()
      .trim();
    if (!name || !icdCode) return acc;
    return upsertCondition(acc, name, icdCode, "icd");
  }, []);
};

const RelativeForm = ({
  formId,
  patientId,
  editing,
  ordinal,
  showOrdinal,
  blockedBySibling,
  onFilledChange,
  onSavingChange,
  onClaimChange,
  siblingClaims,
  onClose,
  // Transcript-mode extensions — all optional; unset preserves standalone.
  initialPayload,
  onSave,
  saving,
  error,
  themeClass,
  lockName,
  // Extra class(es) to append to the Relationship <Select>'s portal
  // menu. Transcript callers pass "ait-menu" so the dropdown clears the
  // modal's z-index (see aiTranscript.scss). Standalone keeps "fh3-menu"
  // alone — the styling this section already relies on.
  menuClassName,
}) => {
  const isEdit = !!editing;
  const p = initialPayload || null;
  const isTranscript = typeof onSave === "function";
  const lockRelationship = !!lockName;

  // Seeded in the initialisers, not from an effect: a sheet mounts once per
  // record, so `editing` can never change under it. Values are taken
  // defensively — `conditions` arriving as anything but an array would throw
  // on .map, and a non-string `description` on .trim.
  const seededRel = p ? seedRelationshipFromPayload(p) : null;
  const [relationship, setRelationship] = useState(() => {
    if (isEdit) return editing.relationship || "other";
    if (seededRel) return seededRel.relationship;
    // A row with no relationship can't be saved as one; "other" at least makes
    // the required "Specify relationship" field appear instead of shipping a
    // silently mislabelled record. Add mode starts on Father and settles onto
    // the first free relationship when the list lands (see loadList).
    return "father";
  });
  const [customRelation, setCustomRelation] = useState(() => {
    if (isEdit) return editing.customRelation || "";
    if (seededRel) return seededRel.customRelation || "";
    return "";
  });
  const [conditions, setConditions] = useState(() => {
    if (isEdit && Array.isArray(editing.conditions)) return editing.conditions;
    if (p) return seedConditionsFromPayload(p.conditions);
    return [];
  });
  const [description, setDescription] = useState(() => {
    if (isEdit && typeof editing.description === "string") return editing.description;
    if (p && typeof p.description === "string") return p.description;
    return "";
  });
  // Transient state driving IcdAutocomplete. Reset to empty after each pick
  // so the same input box can accept a fresh search (multi-select feel).
  // `pickerCode` is only kept so IcdAutocomplete's own `code` prop can
  // highlight the picked row until it collapses; on our side conditions
  // themselves carry the code.
  const [pickerName, setPickerName] = useState("");
  const [pickerCode, setPickerCode] = useState("");
  const [existingRelatives, setExistingRelatives] = useState([]);
  // Load the patient's persisted FH doc in BOTH modes so the
  // "Already added" duplicate hint (Father / Mother / grandparents)
  // fires whether the reviewer is on the standalone rail or on a
  // transcript "+ Add relative" card. Same JIT read the standalone rail
  // already ran.
  const [loadingList, setLoadingList] = useState(true);
  // A transcript-mode ADD seeded by an AI payload has already "chosen"
  // its relationship — the AI heard "my father" and picked `father`.
  // Mark that as touched so the post-fetch first-open-relationship
  // default doesn't clobber it once loadList lands.
  const initiallyTouched = !!(p && p.relationship);
  /** Has the user picked a relationship? Also gates the add-mode default. */
  const [relTouched, setRelTouched] = useState(initiallyTouched);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  // The fetch callback lands outside React's render pass and must see the
  // CURRENT value, so it reads the ref rather than the state.
  const relTouchedRef = useRef(initiallyTouched);

  const pickRelationship = (key) => {
    relTouchedRef.current = true;
    setRelTouched(true);
    setRelationship(key);
  };

  // The duplicate rules judge the CURRENT doc rather than whatever the content
  // column last rendered. `seedDefault` is add-mode's first read only: once the
  // user has picked a relationship, a later refresh must not move it.
  const loadList = useCallback(
    (seedDefault) => {
      const apply = (list) => {
        if (!mounted.current) return;
        setExistingRelatives(list);
        setLoadingList(false);
        if (seedDefault && !relTouchedRef.current) {
          setRelationship(firstOpenRelationship(takenFrom(list)));
        }
      };
      const svc = new FamilyHistoryService();
      svc.list(
        patientId,
        (res) => apply(toRelativeList(res && res.relatives)),
        () => apply([])
      );
    },
    [patientId]
  );

  useEffect(() => {
    loadList(!isEdit);
    // A sibling sheet's save changes what counts as a duplicate. Without this
    // the "Already added" hints on the sheets left open go stale.
    const onRefresh = () => loadList(false);
    EventService.addListener(FAM_V2_REFRESH, onRefresh);
    return () => EventService.removeListener(FAM_V2_REFRESH, onRefresh);
  }, [loadList, isEdit]);

  // Same set the old modal computed from its `existingRelatives` prop. The
  // record under edit is skipped — a Father must not block itself.
  const takenSingles = useMemo(() => {
    const s = new Set();
    for (const r of existingRelatives || []) {
      if (editing && r.relativeId === editing.relativeId) continue;
      if (isSingleRelationship(r.relationship)) s.add(r.relationship);
    }
    // …and what the other OPEN sheets have picked. The server list cannot see
    // an unsaved sibling, so without this two stacked add sheets could both
    // pick Father and both save — and this rule is frontend-only (the backend
    // never rejects a duplicate parent).
    for (const c of siblingClaims || []) {
      if (isSingleRelationship(c)) s.add(c);
    }
    return s;
  }, [existingRelatives, editing, siblingClaims]);

  // Publish this sheet's claim so SIBLING sheets can see it. Update sheets
  // claim nothing — their relationship is the record's own.
  useEffect(() => {
    if (typeof onClaimChange !== "function") return;
    onClaimChange(formId, isEdit ? null : relationship);
  }, [formId, isEdit, relationship, onClaimChange]);

  const isOther = relationship === "other";

  // Hard block — picked relationship is single-only AND already recorded.
  const duplicateBlocked = useMemo(
    () => isSingleRelationship(relationship) && takenSingles.has(relationship),
    [relationship, takenSingles]
  );

  // Guard the free-text "Other" name against (a) shadowing a predefined
  // relationship label and (b) matching another "other" already on file —
  // "another" excluding the record being edited, so re-saving it is allowed.
  const customRelationError = useMemo(() => {
    if (!isOther) return "";
    const name = (customRelation || "").trim();
    if (!name) return "";
    const key = name.toLowerCase();

    const shadowsBuiltin = RELATIONSHIPS.some(
      (r) => r.key !== "other" && r.label.toLowerCase() === key
    );
    if (shadowsBuiltin) {
      return `Pick that relationship directly from the dropdown instead of "Other".`;
    }

    const duplicateOther = (existingRelatives || []).some((r) => {
      if (r.relationship !== "other") return false;
      if (editing && r.relativeId === editing.relativeId) return false;
      return (r.customRelation || "").trim().toLowerCase() === key;
    });
    if (duplicateOther) {
      return `An "Other" relative named "${name}" already exists for this patient.`;
    }
    return "";
  }, [isOther, customRelation, existingRelatives, editing]);

  // An entry must say SOMETHING about the relative — at least one of
  // Conditions or Description (user rule). "No known conditions" belongs in
  // the description rather than an empty record.
  const detailsMissing = conditions.length === 0 && description.trim().length === 0;

  // Does this sheet have anything on it yet? A second "+ Add family member"
  // click must not bury an untouched blank sheet. Unlike the other v3 rails
  // this section has no free-text primary field — relationship is pre-seeded
  // and always has a value — so ANY deliberate mark on the sheet counts.
  // Edit sheets are filled by definition: their record came from the table.
  const filled =
    isEdit ||
    relTouched ||
    conditions.length > 0 ||
    description.trim().length > 0 ||
    pickerName.trim().length > 0 ||
    customRelation.trim().length > 0;
  useEffect(() => {
    if (typeof onFilledChange !== "function") return;
    onFilledChange(formId, filled);
  }, [formId, filled, onFilledChange]);

  const busy = submitting || !!saving;
  const canSubmit =
    !blockedBySibling &&
    // The duplicate rules are only meaningful once the doc has landed; the
    // single-form rail got this for free by not opening until then.
    !loadingList &&
    !busy &&
    !duplicateBlocked &&
    !customRelationError &&
    !detailsMissing &&
    (!isOther || customRelation.trim().length > 0);

  // ICD pick handler — appends the pick to `conditions` and clears the
  // picker so the reviewer can immediately search another. Silently no-ops
  // on picks whose ICD is already in the list (upsertCondition dedups).
  const addIcdPick = (name, icdCode) => {
    setConditions((prev) => upsertCondition(prev, name, icdCode, "icd"));
    setPickerName("");
    setPickerCode("");
  };

  const submit = () => {
    if (!canSubmit) return;
    if (!isTranscript && !patientId) return;
    setFeedback(null);
    const body = {
      relationship,
      customRelation: isOther ? customRelation.trim() : "",
      conditions,
      description: description.trim(),
    };

    // Transcript mode — hand out; caller owns persistence. Skip the local
    // submitting/feedback + FAM_V2_REFRESH emit.
    if (isTranscript) {
      onSave(body);
      return;
    }

    setSubmitting(true);
    if (typeof onSavingChange === "function") {
      onSavingChange(formId, true);
    }
    const svc = new FamilyHistoryService();
    const failed = (err) => {
      // Unmounted means the rail already dropped this sheet, and closeSheet
      // clears the saving flag on the way out — so there is nothing to hand
      // back and no parent state to touch.
      if (!mounted.current) return;
      if (typeof onSavingChange === "function") {
        onSavingChange(formId, false);
      }
      setSubmitting(false);
      setFeedback({ kind: "err", text: (err && err.message) || "Save failed — please retry" });
    };
    const done = () => {
      EventService.emit(FAM_V2_REFRESH);
      if (!mounted.current) return;
      if (typeof onSavingChange === "function") {
        onSavingChange(formId, false);
      }
      // The rail drops this card, which unmounts us — so nothing is reset or
      // re-rendered here. Anything set after this point would be a setState on
      // an unmounted component.
      onClose(formId);
    };
    // Fresh version per mutation, never cached. Sibling sheets are blocked
    // from saving while this is in flight, so the version read here cannot go
    // stale between the read and the write.
    svc.list(
      patientId,
      (fresh) => {
        const withVersion = { ...body, expectedVersion: (fresh && fresh.version) || 0 };
        if (editing) {
          svc.update(patientId, editing.relativeId, withVersion, done, failed);
        } else {
          svc.add(patientId, withVersion, done, failed);
        }
      },
      failed
    );
  };

  // Unique per sheet — stacked cards would otherwise share DOM ids and every
  // label would point at the first card's field.
  const customId = `fh3-custom-${formId}`;
  const condId = `fh3-cond-${formId}`;
  const descId = `fh3-desc-${formId}`;

  const rootClass = ["fh3-form", themeClass || ""].filter(Boolean).join(" ");
  const displayError = feedback && feedback.kind === "err" ? feedback.text : error || "";

  return (
    <div className={rootClass}>
      <div className="fh3-form-head">
        <span className="fh3-form-title">
          {showOrdinal && <span className="zy-ord" aria-hidden="true">{ordinal}</span>}
          {isEdit ? "Edit relative" : "Add relative"}
        </span>
        {/* Which relative — the disabled Select below shows only "Other" for
            a custom relation, so the typed name would otherwise be buried. */}
        {isEdit && (
          <span className="fh3-form-sub">&middot; {safeRelationshipLabel(editing)}</span>
        )}
      </div>

      <div className="fh3-field">
        <label className="fh3-lbl">
          Relationship <span className="fh3-req">*</span>
        </label>
        <Select
          value={relationship}
          options={RELATIONSHIPS}
          getKey={(o) => o.key}
          getLabel={(o) => o.label}
          getHint={(o) =>
            isSingleRelationship(o.key) && takenSingles.has(o.key)
              ? "Already added"
              : ""
          }
          isDisabled={(o) =>
            isSingleRelationship(o.key) && takenSingles.has(o.key)
          }
          menuClassName={`fh3-menu${menuClassName ? ` ${menuClassName}` : ""}`}
          onChange={pickRelationship}
          /* Fixed once saved — same as the modal. Re-pointing an existing
             record at a different person is a delete + re-add, not an edit.
             Transcript re-edit (lockRelationship) also freezes it — the
             identity must not drift between review and save. */
          disabled={isEdit || lockRelationship}
        />
      </div>

      {isOther && (
        <div className="fh3-field">
          <label className="fh3-lbl" htmlFor={customId}>
            Specify relationship <span className="fh3-req">*</span>
          </label>
          <input
            id={customId}
            className={`fh3-input${customRelationError ? " is-error" : ""}`}
            type="text"
            value={customRelation}
            onChange={(e) => setCustomRelation(e.target.value)}
            placeholder="e.g. Maternal cousin"
            disabled={busy || lockRelationship}
            aria-invalid={!!customRelationError}
          />
          {customRelationError && (
            <p className="fam-field-error">{customRelationError}</p>
          )}
        </div>
      )}

      {duplicateBlocked && (
        <div className="fam-warn is-error">
          A record for this relationship already exists for the patient.
          Pick a different relationship to continue.
        </div>
      )}

      <div className="fh3-field fh3-conditions">
        <div className="fh3-lbl-row">
          <label className="fh3-lbl" htmlFor={condId}>
            Conditions{" "}
            <span className="fh3-lbl-hint">
              &middot; optional
            </span>
          </label>
        </div>
        {/* ICD-only picker (no free text). Multi-select: each pick lands in
            `conditions` as a chip and the input clears so the reviewer can
            search another. Rows already picked render greyed with an
            "already added" pill so the user can't add the same ICD twice. */}
        <IcdAutocomplete
          value={pickerName}
          code={pickerCode}
          disabled={busy}
          onChange={({ complaint, complaintCode }) => {
            if (complaintCode && complaintCode.icdCode) {
              addIcdPick(complaint, complaintCode.icdCode);
              return;
            }
            setPickerName(complaint || "");
            setPickerCode("");
          }}
          isAlreadyActive={(r) => {
            const code = (r && (r.icdCode || r.code) || "").toString();
            if (!code) return false;
            return conditions.some((c) => (c.icdCode || "") === code);
          }}
          alreadyActiveTitle="Already added to this relative — remove the chip below to re-pick"
          placeholder="Search a condition"
        />

        {conditions.length > 0 && (
          <div className="fh3-chiprail">
            {conditions.map((c) => (
              <span className="fh3-chip is-on" key={c.icdCode || c.name}>
                <span className="fh3-chip-name">{c.name}</span>
                {c.icdCode && (
                  <span className="fh3-chip-icd" aria-label={`ICD ${c.icdCode}`}>
                    ICD {c.icdCode}
                  </span>
                )}
                <button
                  type="button"
                  className="fh3-chip-x"
                  onClick={() => setConditions(removeCondition(conditions, c.name))}
                  aria-label={`Remove ${c.name}`}
                  disabled={busy}
                >
                  &#10005;
                </button>
              </span>
            ))}
          </div>
        )}

        <span className="fh3-suggest-lbl">
          <span className="fh3-sparkle" aria-hidden="true"><SparkleMarkIcon /></span>
          Common conditions
        </span>
        <div className="fh3-chiprail fh3-suggests">
          {COMMON_CONDITIONS.map((cc) => {
            // Hide from the rail once the same ICD is on the relative — dedup
            // is by icdCode (the picker uses the same key), so a common-pick
            // AND an ICD-search-pick of the same code can't both surface.
            const on = conditions.some(
              (c) =>
                (c.icdCode && c.icdCode === cc.icdCode) ||
                c.name.toLowerCase() === cc.name.toLowerCase()
            );
            if (on) return null;
            return (
              <button
                key={cc.icdCode || cc.name}
                type="button"
                className="fh3-chip"
                onClick={() => addIcdPick(cc.name, cc.icdCode)}
                disabled={busy}
                title={`ICD ${cc.icdCode}`}
              >
                <ChipPlusBoldIcon />
                <span className="fh3-chip-name">{cc.name}</span>
                {cc.icdCode && (
                  <span className="fh3-chip-icd" aria-label={`ICD ${cc.icdCode}`}>
                    ICD {cc.icdCode}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="fh3-field">
        <div className="fh3-lbl-row">
          <label className="fh3-lbl" htmlFor={descId}>
            Description <span className="fh3-lbl-opt">optional</span>
          </label>
          <span className="fh3-lbl-max">
            {description
              ? `${countWords(description)} / ${MAX_DESCRIPTION_WORDS} words`
              : `Max ${MAX_DESCRIPTION_WORDS} words`}
          </span>
        </div>
        <textarea
          id={descId}
          className="fh3-textarea"
          rows={3}
          value={description}
          onChange={(e) =>
            setDescription(clampWords(e.target.value, MAX_DESCRIPTION_WORDS))
          }
          placeholder="Add a short note..."
          disabled={busy}
        />
      </div>

      {detailsMissing && (
        <p className="fh3-hint">
          Add at least one condition, or a description, to save this relative.
        </p>
      )}

      {displayError && <div className="cc-feedback is-err">{displayError}</div>}

      <div className="fh3-foot">
        {/* Stays live while saving ON PURPOSE. A write that never settles
            (the token-refresh path can swallow its callbacks) would otherwise
            pin the stack-wide save lock with no way out — every sheet's Save
            dead and this sheet undismissable until a reload. Closing mid-write
            is safe: the rail clears the lock, and both done() and failed()
            bail on !mounted, so a late response touches nothing. */}
        <button type="button" className="fh3-cancel" onClick={() => onClose(formId)}>
          {busy ? "Close" : "Cancel"}
        </button>
        <button
          type="button"
          className={`fh3-save${themeClass ? ` ${themeClass}` : ""}`}
          onClick={submit}
          disabled={!canSubmit}
          title={blockedBySibling ? "Another relative is saving" : undefined}
        >
          {busy
            ? (isEdit ? "Updating…" : (isTranscript ? "Adding…" : "Saving…"))
            : blockedBySibling
            ? "Waiting…"
            : loadingList
            ? "Loading…"
            : isEdit
            ? "Update"
            : isTranscript
            ? "Add"
            : "Save relative"}
        </button>
      </div>
    </div>
  );
};

RelativeForm.propTypes = {
  /** Stable per-sheet id; namespaces DOM ids and keys the rail's bookkeeping. */
  formId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  patientId: PropTypes.number,
  /** null = blank add; a relative record = editing that record. */
  editing: PropTypes.object,
  /** 1-based position in the stack, top first. */
  ordinal: PropTypes.number,
  showOrdinal: PropTypes.bool,
  /** A sibling sheet is mid-save; ours must wait so the version read is safe. */
  blockedBySibling: PropTypes.bool,
  onFilledChange: PropTypes.func,
  onSavingChange: PropTypes.func,
  /** Publishes this sheet's relationship so siblings can block a duplicate. */
  onClaimChange: PropTypes.func,
  /** Relationships the other open sheets have picked. */
  siblingClaims: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  // Transcript-mode extensions — all optional; unset preserves standalone.
  initialPayload: PropTypes.object,
  onSave: PropTypes.func,
  saving: PropTypes.bool,
  error: PropTypes.string,
  themeClass: PropTypes.string,
  /**
   * Lock the relationship + custom-relation as read-only. Transcript
   * click-to-edit passes true when reopening an already-captured item
   * so the identity can't drift between review and save.
   */
  lockName: PropTypes.bool,
  /** Extra class for the Relationship dropdown menu portal. */
  menuClassName: PropTypes.string,
};

RelativeForm.defaultProps = {
  initialPayload: null,
  onSave: null,
  saving: false,
  error: "",
  themeClass: "",
  lockName: false,
  menuClassName: "",
};

export default RelativeForm;
